import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import {
    Spin,
    Image,
    Tabs,
    TabsProps,
    Form,
    theme,
} from "antd";
import {
    LoadingOutlined,
    UserOutlined
} from "@ant-design/icons";
import PracticesService, {
    ClientReviewerUserOptionsType,
    DefaultMissCallAgent,
    IGeneralSettingsInitialValues,
    IPractice,
    PracticeTabsType,
    PracticeUpdateTypes,
} from "../../Services/PracticesService";
import { adminContext } from "../../Common/PageRoute";
import { PageLayout, PageHeader } from "../../Common/Layout";
import BasicInformation from "./BasicInformation";
import TimeZoneVATAndEmail from "./TimeZoneVATAndEmail";
import PracticeSequence from "./PracticeSequence";
import Features from "./Features";
import GeneralSettings from "./GeneralSettings";
import { PracticeBankAccount } from "./PracticeBankAccount";
import AMLReport from "./AMLReport";
import SubmissionHoldList from "../SubmissionHold/SubmissionHoldList";
import BiometricStats from "./BiometricStats";
import { useUser } from "../../Context/UserContext";
import { usePractice } from "../../Context/PracticeContext";

const defaultData = {
    id: "",
    address: {
        building: "",
        city: "",
        country: "",
        county: "",
        postcode: "",
        street: "",
    },
    params: {
        dueInvoicesTaskDays: 0,
        pageTrackTimeout: 0,
        taskDueDateInDays: 0,
        secureLinkExpiry: 0,
    },
    creditNoteReviewAmount: 0,
    dueDateConfig: {
        number: 0,
        period: 0,
    },
    clientReviewerPracticeProfile: {
        userOption: ClientReviewerUserOptionsType.Unknown,
    },
    assignMissedCallUserOption: DefaultMissCallAgent.Unknown,
    appFeature: {
        accounts: false,
        aml: false,
        bankStatement: false,
        bookkeeping: false,
        calls: false,
        chats: false,
        clientDataRequest: false,
        corporationTax: false,
        dayEnd: false,
        leadSources: false,
        linkedinLeads: false,
        payroll: false,
        teams: false,
        webForms: false,
        timeSheet: false,
        careOf: false,
        selfAssessment: false,
        dms: false,
        audit: false,
        companiesHouseSync: false,
        other1: false,
        other2: false,
        other3: false,
    },
    appSubFeature: {
        accountManagar: false,
        bankDetails: false,
        actionTab: false,
        addCustomer: false,
        digitalSignatureWithoutAML: false,
        manualTimesheet: false,
        progressiveLeads: false,
        websiteQueriesWithOTP: false,
        withoutDiscount: false,
        clientAutomation: false,
        proofOfEarningFPS: false,
        receiptAttachments: false,
        skipTeamUpdate: false,
        secureLink: false,
        amLforAllOfficers: false,
        firstResponse: false,
        accountant: false,
        mode: false,
        agentCredentialsRemark: false,
        clientBrand: false,
        s455TaxLiability: false,
        scheduleEmail: false,
        resourceSection: false,
        allowTLandAdminToUpdateTaskDeadline: false,
        directorAccountWhileRetrieve: false,
        reviewNotificationToTeamleader: false,
        noChangeInTaskAssigneeWithTeamChange: false,
        loeFinancialAutomation: false,
        collectionMethod: false,
        chargeDay: false,
        clientsJoinersAndLeavers: false,
        blockInvoiceEdit: false,
        masking: false,
        xcelify: false,
        clientReferenceInInvoice: false,
        payeMasterReport: false,
        accaBotChat: false,
        preparationSideValidation: false,
        cardNet: false,
        teamSalesComparison: false,
        creditNoteReviewRequest: false,
        reviewAutoRefund: false,
        biometric: false,
        reviewRequestAttachment: false,
        accaBot: false,
        autoComplete: false,
        emailSummarizer: false,
        bookOcrText: false,
        callTranscription: false,
        teamGroup: false,
        teamIsolation: false,
        mtsAutomationSettings: {
            automate: false,
            days: 0,
        },
        noReply: {
            enabled: false,
            replyEmail: "",
        },
        reviewRequestConfig: {
            APCT: false,
            SA100: false,
            SA800: false,
            VAT: false,
        },
    },
    bankAccount: {
        accountName: "",
        accountNumber: "",
        bankName: "",
        bik: "",
        iban: "",
        shortCode: "",
    },
    website: {
        iconUrl: "",
        logoUrl: "",
        url: "",
        urlPrivacy: "",
        urlTnC: "",
    },
    name: "",
    email: "",
    phoneNumber: 0,
    timeZone: "",
    vat: {
        enabled: false,
        rate: 0,
    },
    emailProvider: 0,
    defaultBCCs: [],
    defaultCCs: [],
    sequences: {
        invoice: {
            leadingZero: 0,
            postfix: "",
            prefix: "",
            seperator: "",
            type: 0,
        },
        quote: {
            leadingZero: 0,
            postfix: "",
            prefix: "",
            seperator: "",
            type: 0,
        },
        creditNote: {
            leadingZero: 0,
            postfix: "",
            prefix: "",
            seperator: "",
            type: 0,
        },
        receipt: {
            leadingZero: 0,
            postfix: "",
            prefix: "",
            seperator: "",
            type: 0,
        },
        refund: {
            leadingZero: 0,
            postfix: "",
            prefix: "",
            seperator: "",
            type: 0,
        },
    },
    badges: {
        warningMessage: "",
        dateFrom: "",
        dateTo: "",
        dateRange: [],
        sA100: false,
        sA800: false,
        cis: false,
        vat: false,
        eps: false,
        fps: false,
        confirmationStatement: false,
        corporationTax: false,
        accountsReport: false,
    },
};

const PracticeDetails = ({ data }: any) => {
    const { openNotification } = React.useContext(adminContext);
    const { isSubAdmin } = useUser();
    const isViewOnly = isSubAdmin();
    const [form] = Form.useForm();
    const { id } = useParams<{ id: string }>();
    const practiceId = Number(id);
    const { allPracticeIds } = usePractice();
    const [loading, setLoading] = React.useState(false);
    const [practicesData, setPracticesData] = React.useState<IPractice>(defaultData);
    const [practiceGS, setPracticeGS] = React.useState<IGeneralSettingsInitialValues>({
        name: "",
        email: "",
        logoUrl: "",
        connectionString: "",
        database: "",
        host: "",
        identifier: "",
    });
    const [activeTab, setActiveTab] = React.useState<string>(PracticeTabsType.generalSettings);
    const [isEdit, setIsEdit] = React.useState<{ type: PracticeUpdateTypes }>({
        type: PracticeUpdateTypes.unknown,
    });
    const [refreshBasicInfo, setRefreshBasicInfo] = React.useState(0);

    useEffect(() => {
        setActiveTab(PracticeTabsType.generalSettings);
        setPracticesData(defaultData);
        form.resetFields();
    }, [practiceId]);  // eslint-disable-line react-hooks/exhaustive-deps

    const fetchGeneralSettings = async () => {
        try {
            setLoading(true);
            const res = await PracticesService.getPracticeGeneralSettings(practiceId);
            if (res.status && res?.result) {
                setPracticeGS(res?.result);
            }
        } catch (error) {
            openNotification("error", "Failed to fetch practice details.");
        }
        finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchGeneralSettings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [practiceId]);

    React.useEffect(() => {
        if (practicesData && Object.keys(practicesData).length > 0) {
            form.setFieldsValue(practicesData);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [practicesData]);

    const handleValuesChange = async (changedValues: any, allValues: any) => {
        setPracticesData(allValues);
    };

    const currentPracticeLabel =
        allPracticeIds.find((o) => o.value === practiceId)?.label ||
        `Practice - ${practiceId}`;

    const items: TabsProps["items"] = [
        {
            key: PracticeTabsType.generalSettings,
            label: "Basic Details",
            children: (
                <>
                    <GeneralSettings
                        isEdit={isEdit}
                        setIsEdit={setIsEdit}
                        setLoading={setLoading}
                        generalSettingsData={practiceGS}
                        practiceId={practiceId}
                        setPracticeGS={setPracticeGS}
                    />
                    <BasicInformation
                        isEdit={isEdit}
                        setIsEdit={setIsEdit}
                        setLoading={setLoading}
                        practiceId={practiceId}
                        refreshTrigger={refreshBasicInfo}
                    />
                </>
            ),
        },
        {
            key: PracticeTabsType.bankAccountDetails,
            label: "Bank accounts",
            children: (
                <PracticeBankAccount
                    key={practiceId}
                    practiceId={practiceId}
                    onPrimaryChange={() => setRefreshBasicInfo((prev) => prev + 1)}
                />
            ),
        },
        {
            key: PracticeTabsType.timeZoneVatEmail,
            label: "Time zone, VAT & Email",
            children: (
                <TimeZoneVATAndEmail
                    isEdit={isEdit}
                    setIsEdit={setIsEdit}
                    setLoading={setLoading}
                    practiceId={practiceId}
                />
            ),
        },
        {
            key: PracticeTabsType.sequenceAndReview,
            label: "Sequence & Review",
            children: (
                <>
                    <PracticeSequence
                        isEdit={isEdit}
                        setIsEdit={setIsEdit}
                        setLoading={setLoading}
                        practiceId={practiceId}
                    />

                </>
            ),
        },
        {
            key: PracticeTabsType.features,
            label: "Features",
            children: (
                <Features
                    isEdit={isEdit}
                    setIsEdit={setIsEdit}
                    setLoading={setLoading}
                    practiceId={practiceId}
                />
            ),
        },
        ...(!isViewOnly ? [{
            key: PracticeTabsType.submissionHold,
            label: "Submission Hold",
            children: (
                <SubmissionHoldList />
            ),
        }] : []),
        {
            key: PracticeTabsType.aml,
            label: "AML",
            children: (
                <AMLReport
                    practiceId={practiceId}
                    practiceName={currentPracticeLabel}
                />
            ),
        },
        {
            key: PracticeTabsType.biometric,
            label: "AML-Biometric",
            children: (
                <BiometricStats
                    practiceId={practiceId}
                    practiceName={currentPracticeLabel}
                />
            ),
        },
    ];

    const { token } = theme.useToken();

    const practiceTitle = (
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            {/* Logo/Avatar container */}
            <div
                style={{
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    background: token.colorFillSecondary,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: `1px solid ${token.colorBorder}`,
                    position: "relative",
                    flexShrink: 0,
                    overflow: "hidden"
                }}
            >
                {(practiceGS?.logoUrl || practicesData?.website?.logoUrl) ? (
                    <Image
                        preview={false}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        src={practiceGS?.logoUrl || practicesData?.website?.logoUrl}
                        alt="logo"
                    />
                ) : (
                    <UserOutlined style={{ fontSize: 24, color: token.colorTextSecondary }} />
                )}
                {/* Plus icon badge */}
                <div style={{
                    position: "absolute",
                    bottom: 0,
                    right: 0,
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    background: token.colorPrimary,
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 10,
                    border: `2px solid ${token.colorBgContainer}`
                }}>+</div>
            </div>

            {/* Title text and metadata */}
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 18, fontWeight: 700, color: token.colorText }}>
                        {currentPracticeLabel}
                    </span>

                    {/* Identifier badge */}
                    {practiceGS?.identifier && (
                        <span style={{
                            fontSize: 10,
                            fontWeight: 600,
                            color: token.colorPrimary,
                            backgroundColor: token.colorPrimaryBg,
                            padding: "2px 8px",
                            borderRadius: 4,
                            textTransform: "uppercase",
                            letterSpacing: "0.03em",
                            marginLeft: 4
                        }}>
                            {practiceGS.identifier}
                        </span>
                    )}
                </div>

                {/* Subtitle / Host info */}
                {practiceGS?.host && (
                    <div style={{ fontSize: 12, color: token.colorTextSecondary }}>
                        <span>Host: {practiceGS.host}</span>
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <Spin spinning={loading} indicator={<LoadingOutlined style={{ fontSize: 40 }} spin />}>
            <Form form={form} initialValues={practicesData} onValuesChange={handleValuesChange}>
                <PageLayout>
                    <PageHeader
                        title={practiceTitle}
                    />
                    <div style={{ marginTop: 12 }}>
                        <Tabs
                            destroyInactiveTabPane={true}
                            activeKey={activeTab}
                            size="small"
                            items={items}
                            className="custom-tabs"
                            onChange={(key) => setActiveTab(key)}
                        />
                    </div>
                </PageLayout>
            </Form>
        </Spin>
    );
};

export default PracticeDetails;



